import { useState, useEffect, useMemo } from 'react'
import { dia } from '@joint/core'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { isRelationshipProperty } from '../../../map-editor-features/editor/lib/export/relationshipExtractor'
import { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import { parkingTypeFromLayer, routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import { mergeCellProperties } from '../lib/mergeCellProperties'
import { hugLabelBoxToText } from '../lib/labelTextMetrics'
import styles from './PropertyEditor.module.css'
import LightAngleControl from './LightAngleControl'
import { isLightGeometry } from '../lib/lightFrameGeometry'

// router/DB 호환 8종 주차면 type — router/src/enum/parking.enum.ts ParkingType 와 정렬.
// 한글 라벨로 노출하되 저장 값은 영어 코드. WOMEN_ONLY 의 string value 는
// underscore 포함 ('WOMEN_ONLY') — TS identifier (WOMENONLY) 와 다름.
const PARKING_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'REGULAR', label: '일반 (REGULAR)' },
    { value: 'COMPACT', label: '경차 (COMPACT)' },
    { value: 'HANDICAPPED', label: '장애인 (HANDICAPPED)' },
    { value: 'ELECTRIC', label: '전기차 (ELECTRIC)' },
    { value: 'FAMILY', label: '가족배려 (FAMILY)' },
    { value: 'WOMEN_ONLY', label: '여성전용 (WOMEN_ONLY)' },
    { value: 'VULNERABLE', label: '교통약자 (VULNERABLE)' },
    { value: 'MOTORCYCLE', label: '이륜차 (MOTORCYCLE)' },
]
const PARKING_TYPE_VALUE_SET = new Set(PARKING_TYPE_OPTIONS.map(o => o.value))

/** 소수점 2자리에서 잘라낸다(반올림 없음). 1.989 → 1.98, 1.985 → 1.98, 11.6125 → 11.61 */
function trunc2(n: number): number {
    if (!Number.isFinite(n)) return 0
    return Math.trunc(n * 100) / 100
}

interface PropertyEditorProps {
    element: dia.Element
    onUpdate: (id: string, data: any) => void
    graph: dia.Graph | null
    relationTypes?: Record<string, TemplateRelationType>
}

export function PropertyEditor({
    element,
    onUpdate,
    graph,
    relationTypes
}: PropertyEditorProps) {
    const types = useObjectTypeStore(state => state.types)
    const mappings = useObjectTypeStore(state => state.mappings)
    const layerTypeMappings = useObjectTypeStore(state => state.layerTypeMappings)
    const currentFloorId = useFloorStore(state => state.currentFloor)

    const [position, setPosition] = useState<{ x: number; y: number }>(element.position())
    const [size, setSize] = useState<{ width: number; height: number }>(element.size())
    const [selectedTypeId, setSelectedTypeId] = useState<string>('')
    const [customProperties, setCustomProperties] = useState<Record<string, any>>({})
    const [jsonData, setJsonData] = useState('')
    const [jsonError, setJsonError] = useState<string | null>(null)
    const [isCsvEntity, setIsCsvEntity] = useState(false)
    const [showJson, setShowJson] = useState(false)
    const [addingRelationKey, setAddingRelationKey] = useState<string | null>(null)
    const [pendingSelections, setPendingSelections] = useState<Set<string>>(new Set())

    useEffect(() => {
        setPosition(element.position())
        setSize(element.size())

        const data = element.get('data') || {}
        setJsonData(JSON.stringify(data, null, 2))

        // Check if this is a CSV-imported entity (has entityHandle or layer from CSV)
        const isCSV = !!(data.entityHandle || data.layer)
        setIsCsvEntity(isCSV)

        // Resolve typeId: element data → objectTypeId prop → mappings store (by entityHandle)
        // → layerTypeMappings (by data.layer) — defense-in-depth fallback so reloaded
        // elements still resolve a type even if the entity-level `mappings` store hasn't
        // been hydrated yet (see useEntityMappingRestore).
        let typeId = data.typeId || element.prop('objectTypeId') || ''
        // mappings / layerTypeMappings 가 zustand 초기 hydration 전이면 undefined 일 수 있음.
        // StrictMode double-invoke 시 사이드 이펙트가 빈 store 위에서 돌면서 crash 했던 적이 있다 — 방어적으로 빈 배열로 fallback.
        const safeMappings = Array.isArray(mappings) ? mappings : []
        const safeLayerTypeMappings = Array.isArray(layerTypeMappings) ? layerTypeMappings : []
        if (!typeId && data.entityHandle) {
            const mapping = safeMappings.find(m => m.entityHandle === data.entityHandle)
            if (mapping) {
                typeId = mapping.typeId
            }
        }
        if (!typeId && data.layer && currentFloorId) {
            const layerMapping = safeLayerTypeMappings.find(
                m => String(m.floorId) === String(currentFloorId) && m.layerName === data.layer
            )
            if (layerMapping) {
                typeId = layerMapping.typeId
            }
        }

        // Initialize selected type
        setSelectedTypeId(typeId)

        // Initialize properties with default values from object type.
        //
        // PRESERVATION INVARIANT (preserve-cell-data-on-reload PDCA): start
        // from the FULL `data.properties` object so we keep relationship keys
        // (`linkedXxxIds`, defined by `relationTypes`, NOT by `type.properties`)
        // and any other user-authored fields. Then layer template defaults on
        // top — but only for keys that aren't already set on the element.
        // Never overwrite an existing user value with a default.
        if (typeId) {
            const type = types.find(t => t.id === typeId)
            if (type) {
                const initialProps: Record<string, any> = { ...(data.properties || {}) }
                // type.properties 가 미정의 (구버전 ObjectType / API 응답에서 누락) 일
                // 때 forEach 호출에서 TypeError 가 발생. 안전하게 빈 배열로 fallback.
                ;(Array.isArray(type.properties) ? type.properties : []).forEach(prop => {
                    if (!(prop.key in initialProps) || initialProps[prop.key] == null || initialProps[prop.key] === '') {
                        initialProps[prop.key] = data.properties?.[prop.key] ?? prop.defaultValue ?? ''
                    }
                })
                setCustomProperties(initialProps)
            } else {
                setCustomProperties(data.properties || {})
            }
        } else {
            setCustomProperties(data.properties || {})
        }
    }, [element, types, mappings, layerTypeMappings, currentFloorId])

    const handlePositionChange = (axis: 'x' | 'y', value: string) => {
        const numValue = parseFloat(value)
        if (isNaN(numValue)) return

        const newPos = { ...position, [axis]: numValue }
        setPosition(newPos)
        onUpdate(element.id as string, { position: newPos })
    }

    const handleSizeChange = (dim: 'width' | 'height', value: string) => {
        const numValue = parseFloat(value)
        if (isNaN(numValue) || numValue <= 0) return

        const newSize = { ...size, [dim]: numValue }
        setSize(newSize)
        onUpdate(element.id as string, { size: newSize })
    }

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const typeId = e.target.value
        setSelectedTypeId(typeId)

        // Update element data with new typeId
        const currentData = element.get('data') || {}
        const newData = { ...currentData, typeId }

        // If type changed, we might want to reset properties or keep matching ones
        // For now, let's keep existing properties

        onUpdate(element.id as string, { data: newData })
    }

    const handlePropertyChange = (key: string, value: any) => {
        const newProperties = { ...customProperties, [key]: value }
        setCustomProperties(newProperties)

        // PRESERVATION INVARIANT (preserve-cell-data-on-reload PDCA): merge
        // over the LIVE `data.properties` instead of overwriting with the
        // editor view alone. The editor's `customProperties` is a partial
        // projection (driven by `type.properties`) and does not include
        // relationship keys defined by `relationTypes`. Direct assignment
        // would silently drop those, wiping any relationships the user has
        // created on this element.
        const currentData = element.get('data') || {}
        const currentDataProps = (currentData.properties || {}) as Record<string, unknown>
        const mergedProperties = mergeCellProperties(currentDataProps, newProperties)
        const newData = {
            ...currentData,
            properties: mergedProperties,
        }

        // Update JSON display
        setJsonData(JSON.stringify(newData, null, 2))

        onUpdate(element.id as string, { data: newData })
    }

    const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        setJsonData(value)

        try {
            const parsed = JSON.parse(value)
            setJsonError(null)
            onUpdate(element.id as string, { data: parsed })

            // Sync local state if JSON is edited directly
            if (parsed.typeId) setSelectedTypeId(parsed.typeId)
            if (parsed.properties) setCustomProperties(parsed.properties)
        } catch (err) {
            setJsonError((err as Error).message)
        }
    }

    const selectedType = types.find(t => t.id === selectedTypeId)

    // Build relationship entries from relationTypes (shows all possible, not just existing)
    const allRelationEntries = useMemo(() => {
        if (!relationTypes || !selectedTypeId) return []

        const entries: Array<{
            relationKey: string
            name: string
            propertyKey: string
            targetType: string
            cardinality: string
            linkedIds: string[]
            linkedNames: string[]
        }> = []

        Object.entries(relationTypes).forEach(([key, rel]) => {
            if (rel.sourceType !== selectedTypeId) return

            const props = customProperties || {}
            const value = props[rel.propertyKey]
            const ids = Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : [])

            const names = ids.map((id: string) => {
                if (!graph) return id
                const cell = graph.getCell(id)
                if (!cell) return id
                const cellData = cell.get('data') || {}
                return cellData.properties?.name || id.substring(0, 8)
            })

            entries.push({
                relationKey: key,
                name: rel.name,
                propertyKey: rel.propertyKey,
                targetType: rel.targetType,
                cardinality: rel.cardinality,
                linkedIds: ids,
                linkedNames: names
            })
        })
        return entries
    }, [relationTypes, selectedTypeId, customProperties, graph])

    // Fallback: show relationship properties even without relationTypes
    const relationshipEntries = useMemo(() => {
        if (allRelationEntries.length > 0) return []
        const entries: Array<{ key: string; targetIds: string[]; targetNames: string[] }> = []
        const props = customProperties || {}

        Object.entries(props).forEach(([key, value]) => {
            if (!isRelationshipProperty(key)) return
            const ids = Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : [])
            if (ids.length === 0) return

            const targetNames = ids.map((id: string) => {
                if (!graph) return id
                const cell = graph.getCell(id)
                if (!cell) return id
                const cellData = cell.get('data') || {}
                return cellData.properties?.name || id.substring(0, 8)
            })

            entries.push({ key, targetIds: ids, targetNames })
        })
        return entries
    }, [allRelationEntries, customProperties, graph])

    // Get available targets for a relationship type
    const getAvailableTargets = (targetType: string, linkedIds: string[]) => {
        if (!graph) return []
        const linkedSet = new Set(linkedIds)
        return graph.getElements()
            .filter(el => {
                const d = el.get('data')
                return d?.typeId === targetType && (el.id as string) !== (element.id as string)
            })
            .map(el => {
                const d = el.get('data') || {}
                const id = el.id as string
                return {
                    id,
                    name: d.properties?.name || id.substring(0, 8),
                    linked: linkedSet.has(id)
                }
            })
            .sort((a, b) => {
                if (a.linked !== b.linked) return a.linked ? -1 : 1
                return a.name.localeCompare(b.name, undefined, { numeric: true })
            })
    }

    const handleAddRelations = (propertyKey: string, cardinality: string) => {
        const currentValue = customProperties[propertyKey]
        const existing = Array.isArray(currentValue) ? [...currentValue] : (currentValue ? [currentValue] : [])
        const maxCount = cardinality === 'N' ? Infinity : parseInt(cardinality.split(':')[1] || '1', 10)

        const newIds = [...existing]
        for (const id of pendingSelections) {
            if (!newIds.includes(id) && newIds.length < maxCount) {
                newIds.push(id)
            }
        }

        handlePropertyChange(propertyKey, newIds.length > 0 ? (maxCount === 1 ? newIds[0] : newIds) : undefined)
        setAddingRelationKey(null)
        setPendingSelections(new Set())
    }

    // Get name from properties
    const elementName = customProperties.name || ''

    const handleNameChange = (value: string) => {
        handlePropertyChange('name', value)
        // 텍스트 라벨 객체는 캔버스에 이름을 직접 표시하므로 텍스트 + 선택
        // 박스 크기를 함께 동기화한다 (보이는 글자 == 잡히는 영역).
        if (element.get('data')?.kind === 'label') {
            element.attr('label/text', value)
            hugLabelBoxToText(element)
        }
    }

    const handleRemoveRelation = (propertyKey: string, targetId: string) => {
        const currentValue = customProperties[propertyKey]
        if (Array.isArray(currentValue)) {
            const newValue = currentValue.filter((id: string) => id !== targetId)
            handlePropertyChange(propertyKey, newValue.length > 0 ? newValue : undefined)
        } else if (currentValue === targetId) {
            handlePropertyChange(propertyKey, undefined)
        }
    }

    const data = element.get('data') || {}
    const isGroupContainer = data.isGroup === true

    // 주차면 객체 감지 — selectedType.name 또는 원본 data.layer 가
    // parkingLocation 카테고리로 라우팅되면 true. PropertyEditor 에서
    // router/DB type 컬럼 (5종) 을 select 로 노출하기 위함.
    const isParkingLocation = useMemo(() => {
        const typeName = selectedType?.name || ''
        if (typeName && routeLayerToCategory(typeName) === 'parkingLocation') return true
        const layer = (data.layer as string) || ''
        if (layer && routeLayerToCategory(layer) === 'parkingLocation') return true
        return false
    }, [selectedType, data.layer])

    // 현재 저장된 type 값을 PARKING_TYPE_OPTIONS 중 하나로 정규화.
    // 저장값이 없으면 data.layer 로부터 자동 추정 (csvRenderer 가 이미 채워
    // 두지만 구버전 데이터 호환). 그래도 없으면 REGULAR 기본값.
    const parkingTypeValue = (() => {
        const raw = customProperties.type
        if (typeof raw === 'string') {
            const upper = raw.toUpperCase()
            if (PARKING_TYPE_VALUE_SET.has(upper)) return upper
        }
        return parkingTypeFromLayer(data.layer as string | undefined)
    })()

    if (isGroupContainer) {
        const groupPos = element.position()
        const groupSize = element.size()
        const origCount = data.originalElements?.length || 0

        return (
            <div className={styles.container}>
                <div className={styles.section}>
                    <h3 className={styles.title}>Group</h3>

                    <div className={styles.group}>
                        <label className={styles.groupLabel}>Name</label>
                        <input
                            type="text"
                            value={data.properties?.name || data.groupName || ''}
                            onChange={(e) => {
                                const newName = e.target.value
                                onUpdate(element.id as string, {
                                    data: {
                                        ...data,
                                        groupName: newName,
                                        properties: { ...data.properties, name: newName }
                                    }
                                })
                            }}
                            className={styles.input}
                            placeholder="Enter group name"
                        />
                    </div>

                    <div className={styles.group}>
                        <label className={styles.groupLabel}>Object Type</label>
                        <select
                            value={data.typeId || ''}
                            onChange={(e) => {
                                const typeId = e.target.value || null
                                onUpdate(element.id as string, {
                                    data: { ...data, typeId }
                                })
                                if (typeId) {
                                    element.prop('objectTypeId', typeId)
                                    const matchedType = types.find(t => t.id === typeId)
                                    const color = matchedType?.icon?.startsWith('#')
                                        ? matchedType.icon
                                        : matchedType?.color || null
                                    if (color) {
                                        element.attr('body/fill', 'transparent')
                                        element.attr('body/stroke', color)
                                    }
                                } else {
                                    element.removeProp('objectTypeId')
                                    element.attr('body/fill', 'transparent')
                                    element.attr('body/stroke', '#3B82F6')
                                }
                            }}
                            className={styles.select}
                        >
                            <option value="">None</option>
                            {types.map(type => (
                                <option key={type.id} value={type.id}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {isLightGeometry(element) && <LightAngleControl element={element} onUpdate={onUpdate} />}

                    <div className={styles.group}>
                        <label className={styles.groupLabel}>Position</label>
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label>X</label>
                                <input
                                    type="number"
                                    value={Math.round(groupPos.x)}
                                    onChange={(e) => {
                                        const x = parseFloat(e.target.value)
                                        if (!isNaN(x)) element.position(x, groupPos.y)
                                    }}
                                    className={styles.input}
                                />
                            </div>
                            <div className={styles.field}>
                                <label>Y</label>
                                <input
                                    type="number"
                                    value={Math.round(groupPos.y)}
                                    onChange={(e) => {
                                        const y = parseFloat(e.target.value)
                                        if (!isNaN(y)) element.position(groupPos.x, y)
                                    }}
                                    className={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.group}>
                        <label className={styles.groupLabel}>Size</label>
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label>W</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={trunc2(groupSize.width)}
                                    onChange={(e) => {
                                        const w = parseFloat(e.target.value)
                                        if (!isNaN(w) && w > 0) onUpdate(element.id as string, { size: { width: w, height: groupSize.height } })
                                    }}
                                    className={styles.input}
                                />
                            </div>
                            <div className={styles.field}>
                                <label>H</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={trunc2(groupSize.height)}
                                    onChange={(e) => {
                                        const h = parseFloat(e.target.value)
                                        if (!isNaN(h) && h > 0) onUpdate(element.id as string, { size: { width: groupSize.width, height: h } })
                                    }}
                                    className={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.group}>
                        <label className={styles.groupLabel}>Merged Elements</label>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                            {origCount} elements
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <h3 className={styles.title}>Properties</h3>

                <div className={styles.group}>
                    <label className={styles.groupLabel}>Name</label>
                    <input
                        type="text"
                        value={elementName}
                        onChange={(e) => handleNameChange(e.target.value)}
                        className={styles.input}
                        placeholder="Enter name"
                    />
                </div>

                <div className={styles.group}>
                    <label className={styles.groupLabel}>Object Type</label>
                    <select
                        value={selectedTypeId}
                        onChange={handleTypeChange}
                        className={styles.select}
                    >
                        <option value="">Select Type...</option>
                        {types.map(type => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                    {isCsvEntity && (
                        <span className={styles.mappedBadge}>Mapped from CSV — 변경 시 해당 타입 그룹으로 이동</span>
                    )}
                </div>

                {isParkingLocation && (
                    <div className={styles.group}>
                        <label className={styles.groupLabel}>Parking Type</label>
                        <select
                            value={parkingTypeValue}
                            onChange={(e) => handlePropertyChange('type', e.target.value)}
                            className={styles.select}
                        >
                            {PARKING_TYPE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {selectedType && (
                    <div className={styles.group}>
                        <label className={styles.groupLabel}>
                            {isCsvEntity ? 'Properties' : 'Properties'}
                            {isCsvEntity && <span className={styles.csvNote}> (mapped)</span>}
                        </label>
                        <div className={styles.propertyTable}>
                            {(Array.isArray(selectedType.properties) ? selectedType.properties : [])
                                .filter(prop => prop.key !== 'name') // Name is shown at top
                                .filter(prop => !(isParkingLocation && prop.key === 'type')) // 주차면 type 은 위 dropdown 으로
                                .map(prop => {
                                    const value = customProperties[prop.key]

                                    return (
                                        <div key={prop.key} className={styles.propertyRow}>
                                            <label className={styles.propertyLabel}>
                                                {prop.key}
                                                {prop.required && <span className={styles.required}>*</span>}
                                                <span className={styles.propertyType}>({prop.type})</span>
                                            </label>
                                            <div>
                                                {prop.type === 'boolean' ? (
                                                    <input
                                                        type="checkbox"
                                                        checked={!!value}
                                                        onChange={(e) => handlePropertyChange(prop.key, e.target.checked)}
                                                    />
                                                ) : prop.type === 'number' ? (
                                                    <input
                                                        type="number"
                                                        value={value ?? ''}
                                                        onChange={(e) => {
                                                            const numValue = e.target.value === '' ? '' : parseFloat(e.target.value)
                                                            handlePropertyChange(prop.key, numValue)
                                                        }}
                                                        className={styles.input}
                                                        placeholder={prop.key}
                                                    />
                                                ) : prop.type === 'array' ? (
                                                    <input
                                                        type="text"
                                                        value={Array.isArray(value) ? value.join(', ') : value || ''}
                                                        onChange={(e) => {
                                                            const arrValue = e.target.value.split(',').map(v => v.trim()).filter(v => v)
                                                            handlePropertyChange(prop.key, arrValue)
                                                        }}
                                                        className={styles.input}
                                                        placeholder="comma-separated"
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={value ?? ''}
                                                        onChange={(e) => handlePropertyChange(prop.key, e.target.value)}
                                                        className={styles.input}
                                                        placeholder={prop.key}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                        {(!Array.isArray(selectedType.properties) || selectedType.properties.length === 0) && (
                            <div className={styles.emptyProperties}>No properties</div>
                        )}
                    </div>
                )}

                {isLightGeometry(element) && <LightAngleControl element={element} onUpdate={onUpdate} />}

                <div className={styles.group}>
                    <label className={styles.groupLabel}>Position</label>
                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label>X</label>
                            <input
                                type="number"
                                value={position.x}
                                onChange={(e) => handlePositionChange('x', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                        <div className={styles.field}>
                            <label>Y</label>
                            <input
                                type="number"
                                value={position.y}
                                onChange={(e) => handlePositionChange('y', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.group}>
                    <label className={styles.groupLabel}>Size</label>
                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label>W</label>
                            <input
                                type="number"
                                step="0.01"
                                value={trunc2(size.width)}
                                onChange={(e) => handleSizeChange('width', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                        <div className={styles.field}>
                            <label>H</label>
                            <input
                                type="number"
                                step="0.01"
                                value={trunc2(size.height)}
                                onChange={(e) => handleSizeChange('height', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                    </div>
                </div>

                {(allRelationEntries.length > 0 || relationshipEntries.length > 0) && (
                    <div className={styles.relationsSummary}>
                        <div className={styles.relationsSummaryHeader}>
                            <span className={styles.relationsSummaryTitle}>Relationships</span>
                        </div>
                        <div className={styles.relationsList}>
                            {/* Full relationship entries with add support */}
                            {allRelationEntries.map((entry) => {
                                const isAdding = addingRelationKey === entry.relationKey
                                const targets = isAdding ? getAvailableTargets(entry.targetType, entry.linkedIds) : []

                                return (
                                    <div key={entry.relationKey} style={{ marginBottom: '8px' }}>
                                        <div className={styles.relationItem} style={{ borderBottom: 'none', borderRadius: entry.linkedIds.length > 0 || isAdding ? '4px 4px 0 0' : '4px' }}>
                                            <div className={styles.relationItemLeft}>
                                                <span className={styles.relationItemName}>{entry.name}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span className={styles.relationItemCount}>
                                                    {entry.linkedIds.length}{entry.cardinality !== 'N' ? `/${entry.cardinality.split(':')[1]}` : ''}
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        if (isAdding) {
                                                            setAddingRelationKey(null)
                                                            setPendingSelections(new Set())
                                                        } else {
                                                            setAddingRelationKey(entry.relationKey)
                                                            setPendingSelections(new Set())
                                                        }
                                                    }}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: isAdding ? '#EF4444' : 'var(--color-primary, #3B82F6)',
                                                        cursor: 'pointer',
                                                        fontSize: '15px',
                                                        padding: '0 2px',
                                                        lineHeight: 1,
                                                        fontWeight: 'bold'
                                                    }}
                                                    title={isAdding ? '취소' : '관계 추가'}
                                                >
                                                    {isAdding ? '×' : '+'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Linked targets list */}
                                        {entry.linkedIds.length > 0 && (
                                            <div style={{
                                                background: 'var(--color-bg, #1a1a2e)',
                                                border: '1px solid var(--color-border, #333)',
                                                borderTop: 'none',
                                                borderRadius: isAdding ? '0' : '0 0 4px 4px',
                                                padding: '4px'
                                            }}>
                                                {entry.linkedNames.map((name, i) => (
                                                    <div key={entry.linkedIds[i]} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '3px 8px',
                                                        fontSize: '11px',
                                                        borderRadius: '3px',
                                                        marginBottom: i < entry.linkedIds.length - 1 ? '2px' : 0
                                                    }}>
                                                        <span style={{ color: 'var(--color-text, #e0e0e0)' }}>{name}</span>
                                                        <button
                                                            onClick={() => handleRemoveRelation(entry.propertyKey, entry.linkedIds[i])}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: 'var(--color-text-tertiary, #666)',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                padding: '0 2px',
                                                                lineHeight: 1,
                                                                opacity: 0.6
                                                            }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#EF4444' }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = 'var(--color-text-tertiary, #666)' }}
                                                            title="관계 삭제"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Add relationship panel */}
                                        {isAdding && (
                                            <div style={{
                                                background: 'var(--color-bg, #1a1a2e)',
                                                border: '1px solid var(--color-primary, #3B82F6)',
                                                borderTop: entry.linkedIds.length > 0 ? '1px dashed var(--color-border, #555)' : 'none',
                                                borderRadius: '0 0 4px 4px',
                                                padding: '6px'
                                            }}>
                                                <div style={{
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    marginBottom: '6px'
                                                }}>
                                                    {targets.length === 0 ? (
                                                        <div style={{ fontSize: '11px', color: '#666', padding: '4px 8px', textAlign: 'center' }}>
                                                            대상 객체 없음
                                                        </div>
                                                    ) : targets.map(target => (
                                                        <label key={target.id} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            padding: '3px 8px',
                                                            fontSize: '11px',
                                                            borderRadius: '3px',
                                                            cursor: target.linked ? 'default' : 'pointer',
                                                            opacity: target.linked ? 0.5 : 1,
                                                            background: pendingSelections.has(target.id) ? 'rgba(59,130,246,0.15)' : 'transparent'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={target.linked || pendingSelections.has(target.id)}
                                                                disabled={target.linked}
                                                                onChange={(e) => {
                                                                    const next = new Set(pendingSelections)
                                                                    if (e.target.checked) next.add(target.id)
                                                                    else next.delete(target.id)
                                                                    setPendingSelections(next)
                                                                }}
                                                                style={{ accentColor: 'var(--color-primary, #3B82F6)', width: '13px', height: '13px' }}
                                                            />
                                                            <span style={{ color: target.linked ? '#888' : 'var(--color-text, #e0e0e0)' }}>
                                                                {target.name}
                                                            </span>
                                                            {target.linked && (
                                                                <span style={{ fontSize: '9px', color: '#666', marginLeft: 'auto' }}>연결됨</span>
                                                            )}
                                                        </label>
                                                    ))}
                                                </div>
                                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                                    <button
                                                        onClick={() => { setAddingRelationKey(null); setPendingSelections(new Set()) }}
                                                        style={{
                                                            background: 'transparent',
                                                            border: '1px solid var(--color-border, #444)',
                                                            color: 'var(--color-text, #ccc)',
                                                            borderRadius: '3px',
                                                            padding: '3px 10px',
                                                            fontSize: '11px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        취소
                                                    </button>
                                                    <button
                                                        onClick={() => handleAddRelations(entry.propertyKey, entry.cardinality)}
                                                        disabled={pendingSelections.size === 0}
                                                        style={{
                                                            background: pendingSelections.size > 0 ? 'var(--color-primary, #3B82F6)' : '#333',
                                                            border: 'none',
                                                            color: pendingSelections.size > 0 ? '#fff' : '#666',
                                                            borderRadius: '3px',
                                                            padding: '3px 10px',
                                                            fontSize: '11px',
                                                            cursor: pendingSelections.size > 0 ? 'pointer' : 'default'
                                                        }}
                                                    >
                                                        저장 ({pendingSelections.size})
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}

                            {/* Fallback: show raw relationship properties */}
                            {relationshipEntries.map(({ key, targetIds, targetNames }) => (
                                <div key={key} style={{ marginBottom: '8px' }}>
                                    <div className={styles.relationItem} style={{ borderBottom: 'none', borderRadius: '4px 4px 0 0' }}>
                                        <div className={styles.relationItemLeft}>
                                            <span className={styles.relationItemName}>
                                                {key.replace(/^linked|Ids?$/g, '').replace(/([A-Z])/g, ' $1').trim()}
                                            </span>
                                        </div>
                                        <span className={styles.relationItemCount}>{targetIds.length}</span>
                                    </div>
                                    <div style={{
                                        background: 'var(--color-bg, #1a1a2e)',
                                        border: '1px solid var(--color-border, #333)',
                                        borderTop: 'none',
                                        borderRadius: '0 0 4px 4px',
                                        padding: '4px'
                                    }}>
                                        {targetNames.map((name, i) => (
                                            <div key={targetIds[i]} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '3px 8px',
                                                fontSize: '11px',
                                                borderRadius: '3px',
                                                marginBottom: i < targetIds.length - 1 ? '2px' : 0
                                            }}>
                                                <span style={{ color: 'var(--color-text, #e0e0e0)' }}>{name}</span>
                                                <button
                                                    onClick={() => handleRemoveRelation(key, targetIds[i])}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'var(--color-text-tertiary, #666)',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        padding: '0 2px',
                                                        lineHeight: 1,
                                                        opacity: 0.6
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#EF4444' }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = 'var(--color-text-tertiary, #666)' }}
                                                    title="관계 삭제"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.section}>
                <button
                    onClick={() => setShowJson(!showJson)}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-primary)',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '0',
                        marginBottom: showJson ? '12px' : '0'
                    }}
                >
                    {showJson ? '▼' : '▶'} JSON 미리보기
                </button>

                {showJson && (
                    <>
                        <div className={styles.jsonPreview}>
                            <textarea
                                value={jsonData}
                                onChange={handleJsonChange}
                                className={`${styles.jsonContent} ${jsonError ? styles.error : ''}`}
                                rows={10}
                            />
                        </div>
                        {jsonError && <div className={styles.errorMessage}>{jsonError}</div>}
                    </>
                )}
            </div>
        </div>
    )
}
